import React, { useEffect, useMemo } from "react";
import type { BikeWithLocation } from "../../types/bike";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { useCityStore } from "../../store/useCityStore";
import {
  formatTimeAgo,
  formatSpeed,
  formatBattery,
  getStatusBadgeColor,
  getBatteryColor,
} from "../../utils/formatters";
import {
  MapPin,
  Clock,
  Zap,
  Battery,
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
} from "lucide-react";

interface BikeTableProps {
  bikes: BikeWithLocation[];
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (column: string) => void;
  onEdit: (bike: BikeWithLocation) => void;
  onDelete: (bike: BikeWithLocation) => void;
}

export const BikeTable: React.FC<BikeTableProps> = ({
  bikes,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
}) => {
  const { categories, loadIfNeeded: loadCategoriesIfNeeded } =
    useVehicleCategoryStore();
  const { cities, loadIfNeeded: loadCitiesIfNeeded } = useCityStore();

  useEffect(() => {
    loadCategoriesIfNeeded().catch(console.error);
  }, [loadCategoriesIfNeeded]);

  useEffect(() => {
    loadCitiesIfNeeded().catch(console.error);
  }, [loadCitiesIfNeeded]);

  const categoryNameById = useMemo(
    () =>
      new Map<string, string>(
        categories.map((category) => [
          category.category_id,
          category.category_name,
        ]),
      ),
    [categories],
  );

  const getCategoryLabel = (categoryId?: string | null) => {
    if (categoryId === null || categoryId === undefined) {
      return "-";
    }
    return categoryNameById.get(categoryId) || `Category ${categoryId}`;
  };

  const cityNameById = useMemo(
    () =>
      new Map<string, string>(
        cities.map((city) => [city.city_id, city.city_name]),
      ),
    [cities],
  );

  const getMainCityLabel = (cityId?: string | null) => {
    if (!cityId) {
      return "-";
    }
    return cityNameById.get(cityId) || cityId;
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const getSortableHeaderClass = (column: string) => {
    return `px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${
      sortBy === column ? "bg-gray-100" : ""
    }`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              className={getSortableHeaderClass("bike_id")}
              onClick={() => onSort("bike_id")}
            >
              <div className="flex items-center space-x-1">
                <span>GPS Device ID</span>
                {getSortIcon("bike_id")}
              </div>
            </th>
            <th
              className={getSortableHeaderClass("bike_sn")}
              onClick={() => onSort("bike_sn")}
            >
              <div className="flex items-center space-x-1">
                <span>Bike SN</span>
                {getSortIcon("bike_sn")}
              </div>
            </th>
            <th
              className={getSortableHeaderClass("status")}
              onClick={() => onSort("status")}
            >
              <div className="flex items-center space-x-1">
                <span>Status</span>
                {getSortIcon("status")}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Main City
            </th>
            <th
              className={getSortableHeaderClass("max_speed")}
              onClick={() => onSort("max_speed")}
            >
              <div className="flex items-center space-x-1">
                <span>Max Speed</span>
                {getSortIcon("max_speed")}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Location
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Speed
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Battery
            </th>
            <th
              className={getSortableHeaderClass("last_seen")}
              onClick={() => onSort("last_seen")}
            >
              <div className="flex items-center space-x-1">
                <span>Last Seen</span>
                {getSortIcon("last_seen")}
              </div>
            </th>
            <th
              className={getSortableHeaderClass("created_at")}
              onClick={() => onSort("created_at")}
            >
              <div className="flex items-center space-x-1">
                <span>Created</span>
                {getSortIcon("created_at")}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bikes.map((bike) => (
            <tr
              key={bike.bike_id}
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="px-6 py-3 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {bike.bike_id}
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.bike_sn || "-"}
              </td>
              <td className="px-6 py-3 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(
                    bike.status,
                  )}`}
                >
                  {bike.status}
                </span>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {getCategoryLabel(bike.category_id)}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {getMainCityLabel(bike.primary_city_id)}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.max_speed ?? "-"}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.last_location ? (
                  <a
                    href={`https://www.google.com/maps/search/${bike.last_location.latitude},${bike.last_location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="font-mono text-xs">
                      {bike.last_location.latitude.toFixed(4)},{" "}
                      {bike.last_location.longitude.toFixed(4)}
                    </span>
                  </a>
                ) : (
                  <span className="text-gray-400">No location</span>
                )}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-1">
                  <Zap className="w-4 h-4 text-gray-400" />
                  <span>{formatSpeed(bike.last_location?.speed)}</span>
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm">
                <div className="flex items-center space-x-1">
                  <Battery
                    className={`w-4 h-4 ${getBatteryColor(
                      bike.last_location?.battery_level,
                    )}`}
                  />
                  <span
                    className={getBatteryColor(
                      bike.last_location?.battery_level,
                    )}
                  >
                    {formatBattery(bike.last_location?.battery_level)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                {bike.last_location?.timestamp ? (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span
                      title={new Date(
                        bike.last_location.timestamp,
                      ).toLocaleString()}
                    >
                      {formatTimeAgo(bike.last_location.timestamp)}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400">Never</span>
                )}
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span title={new Date(bike.created_at).toLocaleString()}>
                    {formatTimeAgo(bike.created_at)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(bike);
                    }}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-100 transition-colors"
                    title="Edit bike"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(bike);
                    }}
                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-100 transition-colors"
                    title="Delete bike"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
